import { ProposalDAOInterface, ProposalDataDAOInterface } from '../ProposalDAO';
import { BaseProposalDAO } from '../ProposalDAOImpl';
import DetailedHoldingsDAO from './DetailedHoldingsDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { getPortfoliosForView, createAggregatedPortfolio } from '../../utils/client-level-summary';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';
import DonutData from '@/types/donut-data';
import SeriesData from '@/types/series-data';
import LineSeriesData from '@/types/line-series-data';
import ScatterPlotData from '@/types/scatter-plot-data';

export default class AggregatedTopHoldingsDAO extends BaseProposalDAO implements ProposalDataDAOInterface {
  private detailedHoldingsDAO: DetailedHoldingsDAO | null = null;

  private view: CLIENT_LEVEL_VIEW = CLIENT_LEVEL_VIEW.CURRENT;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new AggregatedTopHoldingsDAO(proposal, proposalElement);
  }

  protected build(): void {
    const daoConfig: ProposalNS.AggregatedTopHoldingsConfig = this.proposalElement.DAO as ProposalNS.AggregatedTopHoldingsConfig;

    // Extract view configuration
    this.view = daoConfig.view;
    this.locale = this.proposal.options.locale;

    // Step 1: Get portfolios for the view
    const portfolios = getPortfoliosForView(this.proposal, this.view);

    if (portfolios.length === 0) {
      return;
    }

    // Step 2: Create aggregated portfolio
    const aggregatedPortfolio = createAggregatedPortfolio(portfolios, this.view);

    // Step 3: Validate aggregated portfolio has required data
    if (!aggregatedPortfolio.holdings || aggregatedPortfolio.holdings.length === 0) {
      return; // No holdings data available
    }

    // Step 4: Create modified proposal with aggregated portfolio
    const aggregatedProposal: ProposalNS.Proposal = {
      ...this.proposal,
      items: [aggregatedPortfolio],
    };

    // Step 5: Create DetailedHoldingsDAO with aggregated proposal
    // Pass through relevant DetailedHoldingsDAO config (filtering only, no grouping/growth needed for donut)
    const detailedHoldingsConfig: ProposalNS.DetailedHoldingsConfig = {
      type: ProposalNS.DAOType.DetailedHoldings,
      filterTopXDAOOptions: daoConfig.filterTopXDAOOptions,
      showSubtotal: false, // Not needed for donut
    };

    const detailedHoldingsElement: ProposalElementNS.ProposalDataElement = {
      ...this.proposalElement,
      DAO: detailedHoldingsConfig,
    };

    this.detailedHoldingsDAO = DetailedHoldingsDAO.buildInstance(aggregatedProposal, detailedHoldingsElement) as DetailedHoldingsDAO;
    this.detailedHoldingsDAO.buildDAO();
  }

  // eslint-disable-next-line class-methods-use-this
  protected postBuild(): void {
    // No post-build logic needed - DetailedHoldingsDAO handles everything
  }

  /**
   * Get donut chart data for aggregated top holdings
   *
   * Returns top N holdings
   * Holdings weights are pre-calculated in createAggregatedPortfolio to sum to 100%
   *
   * @param index Portfolio index (default 0, only one aggregated portfolio exists)
   * @returns DonutData with top holdings or null if no data available
   */
  public getDonutData(index: number = 0): DonutData | null {
    if (!this.detailedHoldingsDAO) {
      return null;
    }

    const donutData = this.detailedHoldingsDAO.getDonutData(index);

    if (!donutData || !donutData.data || !donutData.data[0]) {
      return donutData;
    }

    return donutData;
  }

  /**
   * Get the underlying DetailedHoldingsDAO for advanced usage
   */
  public getDetailedHoldingsDAO(): DetailedHoldingsDAO | null {
    return this.detailedHoldingsDAO;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getColorKeys(): string[] {
    return [];
  }

  // Implement ProposalDataDAOInterface methods
  // eslint-disable-next-line class-methods-use-this
  public getSeriesData(): SeriesData[][] {
    // Not used for donut charts
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getGraphConfigOptions(): ProposalNS.ChartConfigOptions {
    // Not used for donut charts
    return {};
  }

  // eslint-disable-next-line class-methods-use-this
  public getLineSeries(): LineSeriesData[] {
    // Not used for donut charts
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getScatterSeries(): ScatterPlotData[] {
    // Not used for donut charts
    return [];
  }
}
